(function exposeTitanScanLogic(root, factory) {
  const defaultData =
    typeof module === 'object' && module.exports
      ? require('./tps-v2-data.js')
      : root.TitanScanData;
  const runtime =
    typeof module === 'object' && module.exports
      ? require('./tps-v2-runtime.js')
      : root.TitanScanRuntime;
  const stateValidation =
    typeof module === 'object' && module.exports
      ? require('./tps-v2-state-validation.js')
      : root.TitanScanStateValidation;
  const api = stateValidation ? factory(defaultData, runtime, stateValidation) : null;

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.TitanScanLogic = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function createTitanScanLogic(defaultData, runtime, stateValidation) {
  'use strict';

  const MOBILE_UA_PATTERN = /android|iphone|ipad|ipod|mobile|tablet|silk|kindle/i;
  const MIN_REACTION_MS = 1;
  const MAX_REACTION_MS = 1500;
  const MIN_BUDGET = 100000;
  const MAX_BUDGET = 350000;
  const BUDGET_STEP = 10000;
  const LOCAL_PRODUCT_IMAGE_PATTERN = /^assets\/products\/[a-z0-9][a-z0-9-]*\.webp$/i;

  function clamp(value, minimum, maximum) {
    return Math.min(maximum, Math.max(minimum, value));
  }

  function roundTo(value, places) {
    const factor = 10 ** places;
    return Math.round(value * factor) / factor;
  }

  function safeInteger(value, fallback = 0) {
    const number = Number(value);
    return Number.isFinite(number) ? Math.max(0, Math.round(number)) : fallback;
  }

  function isPlainObject(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
  }

  function includesValue(collection, value) {
    return Array.isArray(collection) && collection.includes(value);
  }

  /**
   * Detect the primary input test without changing it mid-test.
   * Coarse pointer plus touch support is the strongest signal; UA and width are fallback evidence.
   */
  function detectInputMode(signals, override = null) {
    if (override === 'mouse' || override === 'touch') {
      return override;
    }

    const source = isPlainObject(signals) ? signals : {};
    const coarsePointer = source.coarsePointer === true;
    const maxTouchPoints = safeInteger(source.maxTouchPoints);
    const viewportWidth = safeInteger(source.viewportWidth, 1920);
    const userAgent = typeof source.userAgent === 'string' ? source.userAgent : '';

    if (coarsePointer && maxTouchPoints > 0) {
      return 'touch';
    }

    if (maxTouchPoints > 0 && viewportWidth <= 900 && MOBILE_UA_PATTERN.test(userAgent)) {
      return 'touch';
    }

    if (viewportWidth <= 540 && MOBILE_UA_PATTERN.test(userAgent)) {
      return 'touch';
    }

    return 'mouse';
  }

  function scoreAim(input) {
    const source = isPlainObject(input) ? input : {};
    const hits = safeInteger(source.hits);
    const misses = safeInteger(source.misses);
    const clicks = hits + misses;
    const durationMs = Number(source.durationMs);
    const validDuration = Number.isFinite(durationMs) && durationMs > 0;
    const accuracy = clicks === 0 ? 0 : Math.round((hits / clicks) * 100);
    const hitsPerSecond = validDuration ? roundTo(hits / (durationMs / 1000), 2) : 0;
    const speedScore = clamp((hitsPerSecond / 3) * 100, 0, 100);
    const score = clicks === 0 ? 0 : Math.round(speedScore * 0.65 + accuracy * 0.35);

    return {
      hits,
      misses,
      clicks,
      accuracy,
      hitsPerSecond,
      score,
    };
  }

  function scoreTap(input) {
    const source = isPlainObject(input) ? input : {};
    const hitTimestamps = Array.isArray(source.hitTimestamps)
      ? source.hitTimestamps.filter(Number.isFinite).map(Number)
      : [];
    const misses = safeInteger(source.misses);
    const taps = hitTimestamps.length;
    const totalTaps = taps + misses;
    const intervals = hitTimestamps
      .slice(1)
      .map((timestamp, index) => timestamp - hitTimestamps[index])
      .filter((interval) => interval > 0 && interval <= 3000);
    const averageInterval = intervals.length
      ? Math.round(intervals.reduce((sum, interval) => sum + interval, 0) / intervals.length)
      : null;
    const fastestInterval = intervals.length ? Math.round(Math.min(...intervals)) : null;
    const durationMs = Number(source.durationMs);
    const validDuration = Number.isFinite(durationMs) && durationMs > 0;
    const accuracy = totalTaps === 0 ? 0 : Math.round((taps / totalTaps) * 100);
    const tapRate = validDuration ? taps / (durationMs / 1000) : 0;
    const countScore = clamp((tapRate / 3.5) * 100, 0, 100);
    const intervalScore =
      averageInterval === null ? 0 : clamp(((550 - averageInterval) / 350) * 100, 0, 100);
    const score =
      totalTaps === 0
        ? 0
        : Math.round(intervalScore * 0.55 + countScore * 0.2 + accuracy * 0.25);

    return {
      taps,
      misses,
      totalTaps,
      accuracy,
      averageInterval,
      fastestInterval,
      tapsPerSecond: roundTo(tapRate, 2),
      score,
    };
  }

  function scoreReaction(samples) {
    const validSamples = Array.isArray(samples)
      ? samples
          .map(Number)
          .filter(
            (sample) =>
              Number.isFinite(sample) && sample >= MIN_REACTION_MS && sample <= MAX_REACTION_MS,
          )
      : [];
    const average = validSamples.length
      ? Math.round(validSamples.reduce((sum, sample) => sum + sample, 0) / validSamples.length)
      : 0;
    const score = average === 0 ? 0 : Math.round(clamp((420 - average) / 2.5, 0, 100));

    return {
      average,
      score,
      validSamples: validSamples.length,
    };
  }

  function sanitizeAnswers(rawAnswers, data = defaultData, allowPartial = true) {
    return stateValidation.sanitizeAnswers(rawAnswers, data, allowPartial);
  }

  function sanitizeMetrics(rawMetrics, data = defaultData) {
    return stateValidation.sanitizeMetrics(rawMetrics, data);
  }

  function validatePersistedState(rawState, data = defaultData) {
    return stateValidation.validatePersistedState(rawState, data);
  }

  function getGame(gameId, data = defaultData) {
    return data.games.find((game) => game.id === gameId) || null;
  }

  function getAmbitionScore(style) {
    return style === 'competitive' ? 100 : style === 'ranked' ? 72 : 46;
  }

  function determineGpuRank(answers, metrics, game) {
    let rank = game.baseGpuRank;

    if (answers.priority === 'quality' && game.qualitySensitivity >= 2) {
      rank += 1;
    }

    if (answers.priority === 'fps') {
      if (answers.monitorHz >= 240 || answers.style === 'competitive') {
        rank = Math.max(rank, 3);
      }
      if (answers.monitorHz >= 360 && game.load !== 'ultra') {
        rank = Math.max(rank, 4);
      }
    }

    if (answers.priority === 'balanced' && answers.monitorHz >= 240) {
      rank = Math.max(rank, 3);
    }

    if (answers.style === 'competitive' && ['light', 'medium'].includes(game.load)) {
      rank = Math.max(rank, 3);
    }

    if (answers.streaming === 'serious' && game.load !== 'light') {
      rank += 1;
    }

    const reaction = scoreReaction(metrics.reactions);
    if (
      metrics.inputScore >= 85 &&
      reaction.average > 0 &&
      reaction.average < 220 &&
      (answers.priority === 'fps' || answers.style === 'competitive')
    ) {
      rank = Math.max(rank, 3);
    }

    return clamp(rank, 1, 6);
  }

  function determineCpu(answers, metrics, game) {
    const reaction = scoreReaction(metrics.reactions);

    if (
      (game.id === 'cyberpunk-2077' && answers.priority === 'quality') ||
      (['heavy', 'ultra'].includes(game.load) && answers.streaming === 'serious')
    ) {
      return 'Ryzen 7 9700X 以上';
    }

    if (
      answers.style === 'competitive' ||
      answers.monitorHz >= 240 ||
      answers.streaming === 'serious' ||
      (answers.priority === 'fps' && reaction.average > 0 && reaction.average < 230)
    ) {
      return 'Ryzen 7 7700 以上';
    }

    if (game.cpuSensitivity >= 3 && answers.style === 'ranked' && answers.monitorHz >= 144) {
      return 'Ryzen 7 5700X 以上';
    }

    return 'Ryzen 5 5500';
  }

  function determineMemory(answers, game) {
    const needs32Gb =
      answers.style === 'competitive' ||
      answers.monitorHz >= 240 ||
      answers.streaming !== 'none' ||
      answers.priority === 'quality' ||
      ['heavy', 'ultra'].includes(game.load);
    return needs32Gb ? 32 : 16;
  }

  function determineMonitor(answers, game) {
    if (answers.priority === 'quality' && game.load === 'ultra') {
      return 'WQHD 165Hz / 4K 144Hz';
    }

    if (answers.priority === 'quality' && game.load === 'heavy') {
      return 'WQHD 165Hz以上';
    }

    if (answers.monitorHz >= 240) {
      return `${answers.monitorHz}Hz以上`;
    }

    if (answers.monitorHz === 60 && answers.style === 'casual') {
      return '60Hz〜144Hz';
    }

    return `${answers.monitorHz}Hz`;
  }

  function isValidProduct(product) {
    return (
      isPlainObject(product) &&
      typeof product.id === 'string' &&
      typeof product.name === 'string' &&
      typeof product.gpu === 'string' &&
      typeof product.cpu === 'string' &&
      Number.isInteger(product.memory) &&
      product.memory > 0 &&
      Number.isInteger(product.gpuRank) &&
      product.gpuRank >= 1 &&
      product.gpuRank <= 6 &&
      Number.isFinite(product.priceMin) &&
      Number.isFinite(product.priceMax) &&
      product.priceMin > 0 &&
      product.priceMax >= product.priceMin &&
      Array.isArray(product.gameLoad) &&
      Number.isFinite(product.targetHz) &&
      typeof product.image === 'string' &&
      (LOCAL_PRODUCT_IMAGE_PATTERN.test(product.image) || runtime?.isShopifyAssetUrl(product.image)) &&
      typeof product.isNew === 'boolean' &&
      typeof product.caseColor === 'string' &&
      product.caseColor.length > 0 &&
      typeof product.description === 'string' &&
      (typeof product.available === 'undefined' || typeof product.available === 'boolean') &&
      /^https:\/\/www\.titan-pc\.net\/products\//.test(product.url)
    );
  }

  function selectableProducts(products) {
    const valid = Array.isArray(products) ? products.filter(isValidProduct) : [];
    const available = valid.filter((product) => product.available !== false);
    return available.length ? available : valid;
  }

  function selectProduct(products, gpuRank, budget, budgetUnlimited = false) {
    const catalog = selectableProducts(products);
    const priceCeiling = budgetUnlimited ? Number.POSITIVE_INFINITY : budget;
    const capable = catalog
      .filter((product) => product.gpuRank >= gpuRank)
      .sort((left, right) => left.priceMin - right.priceMin || left.gpuRank - right.gpuRank);
    const matching = capable.filter((product) => product.priceMin <= priceCeiling);

    if (matching.length) {
      return matching[0];
    }

    if (capable.length) {
      return capable[0];
    }

    return catalog.sort((left, right) => right.gpuRank - left.gpuRank || left.priceMin - right.priceMin)[0] || null;
  }

  function formatYen(value) {
    return `${Math.round(value).toLocaleString('ja-JP')}円`;
  }

  function playerClass(total) {
    if (total >= 88) return 'MASTER';
    if (total >= 75) return 'DIAMOND';
    if (total >= 60) return 'PLATINUM';
    if (total >= 45) return 'GOLD';
    return 'ROOKIE';
  }

  function playerProfile(answers, inputScore, reactionScore) {
    if (answers.streaming === 'serious') {
      return {
        type: 'HYBRID CREATOR',
        summary: 'プレイ性能と制作負荷を同時に考える、配信・編集対応型のプレイヤーです。',
      };
    }

    if (answers.priority === 'quality') {
      return {
        type: 'IMMERSION STRATEGIST',
        summary: '速さだけでなく映像表現と没入感を重視するプレイヤーです。',
      };
    }

    if (inputScore >= 82 && reactionScore >= 78) {
      return {
        type: 'PRECISION FRAGGER',
        summary: '反応と入力精度が高く、撃ち合いで先手を取りやすいプレイヤーです。',
      };
    }

    if (reactionScore >= 72) {
      return {
        type: 'HIGH SPEED ATTACKER',
        summary: '素早い判断と初動を武器に、テンポよく主導権を取るプレイヤーです。',
      };
    }

    if (inputScore >= 55) {
      return {
        type: 'BALANCED FIGHTER',
        summary: '入力精度と判断速度のバランスがよく、幅広いゲームに適応できます。',
      };
    }

    return {
      type: 'TACTICAL GROWTH',
      summary: '立ち回りを活かしながら、安定した環境で伸びていくプレイヤーです。',
    };
  }

  function buildComments(answers, metrics, game, recommendation, reaction) {
    const comments = [];

    if (reaction.average && reaction.average < 220) {
      comments.push('反応速度が高く、撃ち合いで先手を取りやすい数値です。');
    } else if (reaction.average) {
      comments.push('反応速度だけでなく、安定したフレームレートが判断の再現性を支えます。');
    }

    if (answers.monitorHz >= 240) {
      comments.push(
        `現在の${answers.monitorHz}Hzモニターを活かすには、${recommendation.gpu}と${recommendation.cpu}が目安です。`,
      );
    }

    if (answers.priority === 'fps') {
      comments.push('画質よりFPSを重視しているため、GPUだけでなくCPU性能も重要です。');
    } else if (answers.priority === 'quality') {
      comments.push(`${game.name}の映像負荷を優先し、プレイヤースコアが低くてもGPU要件は下げていません。`);
    }

    if (answers.streaming === 'serious') {
      comments.push('配信・編集の同時処理を見込み、メモリ32GBとRyzen 7クラスを優先します。');
    } else if (answers.streaming === 'occasional') {
      comments.push('軽い配信・編集の余力を確保するため、メモリ32GBを優先します。');
    }

    if (answers.budgetUnlimited && recommendation.product.gpuRank < recommendation.gpuRank) {
      comments.push('性能条件に一致する掲載モデルがないため、最も近いGPU構成を表示しています。');
    } else if (recommendation.budgetDelta > 0) {
      comments.push(
        `最も近い掲載モデルは選択予算より${formatYen(recommendation.budgetDelta)}上です。商品ページで現在価格を確認してください。`,
      );
    } else if (recommendation.product.gpuRank < recommendation.gpuRank) {
      comments.push('表示商品は予算内の近似候補です。診断推奨GPUへ上げる場合は予算調整が必要です。');
    }

    return comments.slice(0, 4);
  }

  function buildDiagnosis(state, data = defaultData) {
    if (!isPlainObject(state)) {
      throw new TypeError('Diagnosis state must be an object.');
    }

    const answers = sanitizeAnswers(state.answers, data, false);
    const metrics = sanitizeMetrics(state.metrics, data);
    const game = answers ? getGame(answers.game, data) : null;

    if (!answers || !metrics || !game || metrics.reactions.length !== 3) {
      throw new TypeError('Diagnosis state is incomplete or invalid.');
    }

    const reaction = scoreReaction(metrics.reactions);
    const ambition = getAmbitionScore(answers.style);
    const classScore = Math.round(
      metrics.inputScore * 0.4 + reaction.score * 0.3 + metrics.accuracy * 0.15 + ambition * 0.15,
    );
    const gpuRank = determineGpuRank(answers, metrics, game);
    const aiProduct = selectableProducts(data.products).find(
      (candidate) =>
        candidate.id === state.recommendedProductId &&
        candidate.gpuRank >= gpuRank,
    );
    const product = aiProduct || selectProduct(data.products, gpuRank, answers.budget, answers.budgetUnlimited);

    if (!product) {
      throw new Error('No valid TITAN product data is available.');
    }

    const recommendation = {
      gpuRank,
      gpu: product.gpu,
      cpu: determineCpu(answers, metrics, game),
      memoryGb: determineMemory(answers, game),
      monitor: determineMonitor(answers, game),
      priceBand: {
        min: product.priceMin,
        max: product.priceMax,
        label: product.priceMin === product.priceMax
          ? formatYen(product.priceMin)
          : `${formatYen(product.priceMin)}〜${formatYen(product.priceMax)}`,
      },
      product,
      selectionSource: aiProduct ? 'ai' : 'rules',
      budgetDelta: answers.budgetUnlimited ? 0 : Math.max(0, product.priceMin - answers.budget),
    };
    const profile = playerProfile(answers, metrics.inputScore, reaction.score);
    const budgetReadiness = answers.budgetUnlimited
      ? 30
      : ((answers.budget - MIN_BUDGET) / (MAX_BUDGET - MIN_BUDGET)) * 30;
    const environment = Math.round(clamp((answers.monitorHz / 360) * 70 + budgetReadiness, 0, 100));

    return {
      playerClass: playerClass(classScore),
      classScore,
      playerType: profile.type,
      summary: profile.summary,
      inputLabel: metrics.mode === 'touch' ? 'TAP SCORE' : 'AIM SCORE',
      inputScore: metrics.inputScore,
      reactionAverage: reaction.average,
      reactionScore: reaction.score,
      accuracy: metrics.accuracy,
      deviceLabel: metrics.mode === 'touch' ? 'TOUCH / TAP' : 'PC / MOUSE',
      gameName: game.name,
      recommendation,
      comments: buildComments(answers, metrics, game, recommendation, reaction),
      radar: {
        labels: [metrics.mode === 'touch' ? 'TAP' : 'AIM', 'REACTION', 'ACCURACY', 'AMBITION', 'SETUP'],
        values: [metrics.inputScore, reaction.score, metrics.accuracy, ambition, environment],
      },
    };
  }

  function indexOfOrThrow(collection, value, field) {
    const index = collection.indexOf(value);
    if (index < 0) {
      throw new TypeError(`Invalid ${field}.`);
    }
    return index;
  }

  function toBase36(value) {
    return safeInteger(value).toString(36);
  }

  function fromBase36(value) {
    if (typeof value !== 'string' || !/^[0-9a-z]+$/i.test(value)) {
      return null;
    }
    const parsed = Number.parseInt(value, 36);
    return Number.isSafeInteger(parsed) ? parsed : null;
  }

  function encodeResultHash(state, data = defaultData) {
    const answers = sanitizeAnswers(state.answers, data, false);
    const metrics = sanitizeMetrics(state.metrics, data);
    const deviceMode = state.device && state.device.mode;

    if (!answers || !metrics || !includesValue(data.inputModes, deviceMode)) {
      throw new TypeError('Cannot encode an incomplete result.');
    }

    const parts = [
      toBase36(data.schemaVersion),
      toBase36(indexOfOrThrow(data.games.map((game) => game.id), answers.game, 'game')),
      toBase36(indexOfOrThrow(data.styles, answers.style, 'style')),
      toBase36(indexOfOrThrow(data.monitorOptions, answers.monitorHz, 'monitor')),
      toBase36((answers.budget - MIN_BUDGET) / BUDGET_STEP),
      toBase36(indexOfOrThrow(data.priorities, answers.priority, 'priority')),
      toBase36(indexOfOrThrow(data.streamingOptions, answers.streaming, 'streaming')),
      toBase36(indexOfOrThrow(data.inputModes, deviceMode, 'device')),
      toBase36(metrics.hits),
      toBase36(metrics.misses),
      toBase36(metrics.accuracy),
      toBase36(metrics.inputScore),
      metrics.averageInterval === null ? 'x' : toBase36(metrics.averageInterval),
      metrics.fastestInterval === null ? 'x' : toBase36(metrics.fastestInterval),
      metrics.reactions.map(toBase36).join('-'),
      toBase36(answers.budgetUnlimited ? 1 : 0),
      toBase36(state.recommendedProductId
        ? indexOfOrThrow(data.products.map((product) => product.id), state.recommendedProductId, 'product') + 1
        : 0),
    ];

    return `#scan=${parts.join('.')}`;
  }

  function decodeResultHash(hash, data = defaultData) {
    try {
      if (typeof hash !== 'string' || hash.length > 256 || !hash.startsWith('#scan=')) {
        return null;
      }

      const parts = hash.slice(6).split('.');
      if (![15, 16, 17].includes(parts.length)) {
        return null;
      }

      const version = fromBase36(parts[0]);
      const gameIndex = fromBase36(parts[1]);
      const styleIndex = fromBase36(parts[2]);
      const monitorIndex = fromBase36(parts[3]);
      const budgetStep = fromBase36(parts[4]);
      const priorityIndex = fromBase36(parts[5]);
      const streamingIndex = fromBase36(parts[6]);
      const modeIndex = fromBase36(parts[7]);
      const hits = fromBase36(parts[8]);
      const misses = fromBase36(parts[9]);
      const accuracy = fromBase36(parts[10]);
      const inputScore = fromBase36(parts[11]);
      const averageInterval = parts[12] === 'x' ? null : fromBase36(parts[12]);
      const fastestInterval = parts[13] === 'x' ? null : fromBase36(parts[13]);
      const reactions = parts[14].split('-').map(fromBase36);
      const budgetUnlimited = parts.length >= 16 ? fromBase36(parts[15]) : 0;
      const productValue = parts.length === 17 ? fromBase36(parts[16]) : 0;
      const productIndex = productValue === null ? null : productValue - 1;

      if (
        version !== data.schemaVersion ||
        [gameIndex, styleIndex, monitorIndex, budgetStep, priorityIndex, streamingIndex, modeIndex, hits, misses, accuracy, inputScore].some(
          (value) => value === null,
        ) ||
        reactions.length !== 3 ||
        reactions.some((value) => value === null) ||
        ![0, 1].includes(budgetUnlimited) ||
        productValue === null ||
        productValue > data.products.length
      ) {
        return null;
      }

      const shareable = {
        answers: {
          game: data.games[gameIndex] && data.games[gameIndex].id,
          style: data.styles[styleIndex],
          monitorHz: data.monitorOptions[monitorIndex],
          budget: MIN_BUDGET + budgetStep * BUDGET_STEP,
          budgetUnlimited: budgetUnlimited === 1,
          priority: data.priorities[priorityIndex],
          streaming: data.streamingOptions[streamingIndex],
        },
        device: { mode: data.inputModes[modeIndex] },
        metrics: {
          mode: data.inputModes[modeIndex],
          hits,
          misses,
          accuracy,
          inputScore,
          averageInterval,
          fastestInterval,
          reactions,
        },
        recommendedProductId: productIndex >= 0 ? data.products[productIndex].id : null,
      };

      const sanitizedAnswers = sanitizeAnswers(shareable.answers, data, false);
      const sanitizedMetrics = sanitizeMetrics(shareable.metrics, data);
      if (
        !sanitizedAnswers ||
        !sanitizedMetrics ||
        shareable.answers.budget < MIN_BUDGET ||
        shareable.answers.budget > MAX_BUDGET ||
        !shareable.device.mode ||
        sanitizedMetrics.reactions.length !== 3
      ) {
        return null;
      }

      return {
        answers: sanitizedAnswers,
        device: shareable.device,
        metrics: sanitizedMetrics,
        recommendedProductId: shareable.recommendedProductId,
      };
    } catch (error) {
      return null;
    }
  }

  return Object.freeze({
    detectInputMode,
    scoreAim,
    scoreTap,
    scoreReaction,
    sanitizeAnswers,
    sanitizeMetrics,
    validatePersistedState,
    determineGpuRank,
    selectProduct,
    buildDiagnosis,
    encodeResultHash,
    decodeResultHash,
    formatYen,
  });
});
